import { Toaster as Sonner, type ToasterProps } from 'sonner';

/**
 * The toast layer is INFORMATIONAL ONLY, and therefore must never be
 * hit-testable — hence `pointer-events-none` on the toaster list.
 *
 * Nothing in this app puts an interactive affordance on a toast: every caller
 * (`showNotificationToast` / `showErrorToast` and the ad-hoc `toast.*` calls in
 * dialogs and views) passes only a title + description — no `action`, no
 * `cancel`, no `closeButton`. The durable, interactive surface for the same
 * content is the Notifications view (`notification-row` / `error-entry`).
 *
 * Left hit-testable, the layer breaks the app's primary action. Sonner's
 * default position is `bottom-right`, which is exactly where the chat
 * composer's Send button sits, so the toast raised when an agent turn completes
 * lands ON the button the user needs next. Worse, it does not clear: sonner
 * pauses every toast's auto-dismiss timer while the pointer is inside the list
 * (`onMouseEnter`/`onMouseMove` → `setExpanded(true)`; the per-toast timer
 * effect is `if (expanded || interacting || isDocumentHidden) pauseTimer()`),
 * and the only reset — `if (toasts.length <= 1) setExpanded(false)` — is keyed
 * on the toast array, which hovering never mutates. So moving the mouse onto
 * Send is itself what freezes the toast covering it, and the button stays
 * unclickable for as long as the pointer rests there. That is a deadlock, not a
 * delay: it is why `chat-model-selection` / `sessions` / `web-realtime-bridge`
 * lost every attempt with `<li data-sonner-toast data-expanded="true"> …
 * intercepts pointer events` on `[data-testid="send-button"]`.
 *
 * Repositioning is NOT a fix: it relocates the collision (every corner of this
 * full-bleed shell holds something — top-right is the title-bar group with the
 * logout button, bottom-left is the Shogun launcher) and leaves the
 * hover-freeze intact wherever it lands. Making the layer non-hit-testable
 * removes both the interception AND the freeze at the root, because
 * `onMouseEnter` can no longer fire, so `expanded` stays false and toasts
 * always expire on schedule.
 *
 * `pointer-events` is an inherited CSS property and sonner's stylesheet never
 * sets it back to `auto` on a toast, so declaring it once on the list covers
 * every toast and every descendant. Keyboard access is unaffected (sonner's
 * alt+T focus hotkey and Escape are keydown handlers on `document`).
 */
function Toaster({ className, ...props }: ToasterProps) {
  return (
    <Sonner
      className={`toaster group pointer-events-none${className ? ` ${className}` : ''}`}
      toastOptions={{
        classNames: {
          toast:
            'group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg',
          description: 'group-[.toast]:text-muted-foreground',
          actionButton: 'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground',
          cancelButton: 'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground',
        },
      }}
      {...props}
    />
  );
}

export { toast } from 'sonner';
export { Toaster };
